import React, { useEffect, useState } from "react";
import { Search, Loader2, CheckCircle2, Package, ChefHat, Truck, Flag, Factory, ExternalLink } from "lucide-react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";

const FOOD_STEPS = ["Order Received", "Order Confirmed", "In the Kitchen", "Ready for Pickup / Out for Delivery", "Completed"];
const FOOD_ICONS = [Package, CheckCircle2, ChefHat, Truck, Flag];
const foodNorm = (s) => (s === "Ready for Pickup" || s === "Out for Delivery") ? "Ready for Pickup / Out for Delivery" : s;

const MERCH_STEPS = ["Order Received", "Order Confirmed", "In Production", "Shipped", "Delivered"];
const MERCH_ICONS = [Package, CheckCircle2, Factory, Truck, Flag];
const merchNorm = (o) => {
  const s = o.status;
  if (["Shipped"].includes(s) || o.fulfillment_status === "Shipped") return "Shipped";
  if (["Delivered", "Completed"].includes(s) || o.fulfillment_status === "Delivered") return "Delivered";
  if (s === "In Production") return "In Production";
  if (s === "Order Confirmed") return "Order Confirmed";
  return "Order Received";
};

export default function TrackOrderPage() {
  const [num, setNum] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const track = async (value) => {
    const n = (value ?? num).trim();
    if (!n) return;
    setLoading(true);
    try {
      const r = await api.get(`/orders/${n}`);
      setOrder(r.data);
    } catch {
      setOrder(null);
      toast.error("Order not found. Check your order number.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const last = localStorage.getItem("skk_last_order");
    if (last) { setNum(last); track(last); }
    // eslint-disable-next-line
  }, []);

  const isMerch = order?.order_kind === "merch";
  const STEPS = isMerch ? MERCH_STEPS : FOOD_STEPS;
  const ICONS = isMerch ? MERCH_ICONS : FOOD_ICONS;
  const activeIndex = order ? STEPS.indexOf(isMerch ? merchNorm(order) : foodNorm(order.status)) : -1;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
      <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl text-center">Track Your Order</h1>
      <p className="text-center text-[#FDFBF7]/60 mt-3 text-sm">Enter your order number to see live status — food orders and merch shipments both work here.</p>
      <div className="flex gap-2 mt-8 max-w-md mx-auto">
        <input data-testid="track-order-input" value={num} onChange={(e) => setNum(e.target.value)} onKeyDown={(e) => e.key === "Enter" && track()} placeholder="Order number e.g. M0918-A1B2" className="flex-1 bg-[#181D1A] rounded-full px-5 py-3.5 border border-emerald-800/50 outline-none focus:border-amber-400" />
        <button data-testid="track-order-submit" onClick={() => track()} className="px-6 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase flex items-center gap-2 active:scale-95 transition-transform">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
        </button>
      </div>

      {order && (
        <div className="mt-10 rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-6 sm:p-8" data-testid="track-order-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-wider text-amber-400 font-bold">Order #{order.order_number}</div>
              <div className="font-display font-black text-2xl">{isMerch ? "Merch Shipment" : (order.fulfillment_type === "delivery" ? "Delivery" : "Pickup")} · {money(order.total)}</div>
            </div>
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${order.payment_status === "paid" ? "bg-emerald-950 text-emerald-400 border border-emerald-700/50" : "bg-amber-950 text-amber-400 border border-amber-700/50"}`}>{order.payment_status === "paid" ? "Paid" : "Payment Pending"}</span>
          </div>

          <div className="mt-8 space-y-1">
            {STEPS.map((s, i) => {
              const Icon = ICONS[i];
              const done = i <= activeIndex;
              return (
                <div key={s} className="flex items-center gap-4">
                  <div className="flex flex-col items-center">
                    <span className={`grid place-items-center w-10 h-10 rounded-full ${done ? "bg-amber-400 text-[#0A2818]" : "bg-[#101311] text-[#FDFBF7]/30 border border-emerald-900/60"}`}><Icon className="w-5 h-5" /></span>
                    {i < STEPS.length - 1 && <span className={`w-0.5 h-8 ${i < activeIndex ? "bg-amber-400" : "bg-emerald-900/60"}`} />}
                  </div>
                  <span className={`font-display font-bold uppercase tracking-wide ${done ? "text-[#FDFBF7]" : "text-[#FDFBF7]/40"} ${i === activeIndex ? "text-amber-400" : ""}`}>{s}</span>
                </div>
              );
            })}
          </div>

          {isMerch && order.tracking_number && (
            <div className="mt-8 rounded-2xl bg-[#0A2818] border border-emerald-700/40 p-5" data-testid="merch-tracking-info">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-bold">Shipment Tracking</div>
              <div className="mt-1 text-sm text-[#FDFBF7]/85">{order.carrier ? `${order.carrier} · ` : ""}<span className="font-bold">{order.tracking_number}</span></div>
              {order.tracking_url && (
                <a data-testid="merch-tracking-link" href={order.tracking_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase text-xs">
                  Track Package <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          )}

          {isMerch && !order.tracking_number && order.payment_status === "paid" && (
            <div className="mt-6 text-sm text-[#FDFBF7]/60" data-testid="merch-tracking-pending">Your order is being produced. A tracking number will appear here (and land in your inbox) as soon as it ships. 🚚</div>
          )}

          {isMerch && (order.address || order.city) && (
            <div className="mt-6 border-t border-emerald-900/50 pt-4 text-sm text-[#FDFBF7]/70">
              <div className="text-xs uppercase tracking-wider text-[#FDFBF7]/40 font-bold mb-1">Shipping to</div>
              {order.customer_name}<br />{order.address}{order.city ? `, ${order.city}` : ""} {order.state} {order.zip_code}
            </div>
          )}

          <div className="mt-8 border-t border-emerald-900/50 pt-4 space-y-1.5 text-sm">
            {order.items.map((it, i) => (
              <div key={`${it.item_id || it.product_id || it.name}-${it.size || ""}-${it.color || ""}-${it.spice_level || ""}-${i}`} className="flex justify-between text-[#FDFBF7]/75"><span>{it.quantity}× {it.name}{it.size ? ` (${it.size}${it.color ? "/" + it.color : ""})` : ""}{it.spice_level ? ` (${it.spice_level})` : ""}</span><span>{money(it.line_total)}</span></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
